import { styled } from "@linaria/react";
import { reviewIcons, reviewStatusOptions } from "@/components/workspace/review-status";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

const StatusInput = styled.div`
  width: 188px;
  max-width: 100%;
  min-width: 0;
`;

import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Flex, { AlignItems } from "@galaxy-io/dls/layout/Flex";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
export type Review = {
  state: string;
};
export function useReviews(keys: string[]) {
  const signature = JSON.stringify(keys);
  const [states, setStates] = useState<Record<string, Review>>({}),
    [loadedSignature, setLoadedSignature] = useState(""),
    [busy, setBusy] = useState(false);
  const router = useRouter();
  const { toast } = useToast();
  useEffect(() => {
    let stopped = false;
    if (signature === "[]") return;
    async function load() {
      try {
        const data = await unwrap(
          apiClient().POST("/reviews/states", { body: JSON.parse(signature) }),
        );
        if (stopped) return;
        setStates(data);
        setLoadedSignature(signature);
      } catch (e) {
        if (!stopped)
          toast({
            variant: ToastVariant.ERROR,
            header: "Processing status unavailable",
            description: (e as Error).message,
            ...toastDefaults(ToastVariant.ERROR),

            action: { label: "Retry", onClick: () => void load() },
          });
      }
    }
    // Senders hand us the new states on the event, so paint those before the
    // refetch lands — otherwise the row a person just sent keeps its old status
    // for a round trip.
    function onChanged(event: Event) {
      const detail = (event as CustomEvent<Record<string, Review> | undefined>).detail;
      if (detail) setStates((s) => ({ ...s, ...detail }));
      void load();
    }
    void load();
    window.addEventListener("records:changed", onChanged);
    return () => {
      window.removeEventListener("records:changed", onChanged);
      stopped = true;
    };
  }, [signature, toast]);
  // Table column definitions close over this, and a new identity there remounts
  // every cell, so it has to survive a render.
  const act = useCallback(
    async function act(
      selected: string[],
      action: "process" | "ignore" | "reset" | "acknowledge" | "draft" | "save",
    ) {
      setBusy(true);
      try {
        const data = await unwrap(
          apiClient().POST("/reviews", { body: { keys: selected, action } }),
        );
        setStates((s) => ({ ...s, ...data }));
        window.dispatchEvent(new CustomEvent("records:changed", { detail: data }));
        router.refresh();
        return true;
      } catch (e) {
        toast({
          variant: ToastVariant.ERROR,
          header: "Could not update selection",
          description: (e as Error).message,
          ...toastDefaults(ToastVariant.ERROR),
        });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [router, toast],
  );
  return {
    states,
    loaded: signature === "[]" || loadedSignature === signature,
    busy,
    act,
  };
}
export function ReviewButtons({
  review,
  busy,
  onAction,
  compact = false,
}: {
  review?: Review;
  compact?: boolean;
  busy: boolean;
  onAction: (action: "process" | "ignore" | "reset" | "acknowledge" | "draft" | "save") => void;
}) {
  const unprocessed =
    !review || ["acknowledged", "drafted", "saved", "unprocessed"].includes(review.state);
  return (
    <Flex gap={4} alignItems={AlignItems.CENTER}>
      {unprocessed && (
        <Button
          label={compact ? undefined : "Process"}
          ariaLabel="Process"
          variant={compact ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
          icon={reviewIcons.processed}
          size={ButtonSize.MEDIUM}
          isDisabled={busy}
          onClick={() => onAction("process")}
          tooltip="Process"
        />
      )}
      {unprocessed && (
        <Button
          label={compact ? undefined : "Ignore"}
          ariaLabel="Ignore"
          icon={reviewIcons.ignored}
          size={ButtonSize.MEDIUM}
          variant={compact ? ButtonVariant.SECONDARY : ButtonVariant.TERTIARY}
          isDisabled={busy}
          onClick={() => onAction("ignore")}
          tooltip="Ignore"
        />
      )}
    </Flex>
  );
}
export function ReviewStatus({ review }: { review?: Review; compact?: boolean }) {
  return <Status value={review?.state || "unprocessed"} />;
}
export function RecordReview({ recordKey }: { recordKey: string }) {
  const r = useReviews([recordKey]);
  return (
    <>
      <ReviewStatus review={r.states[recordKey]} />
      <ReviewButtons
        review={r.states[recordKey]}
        busy={r.busy || !r.loaded}
        onAction={(a) => r.act([recordKey], a)}
      />
    </>
  );
}

export function ReviewStatusEditor({
  review,
  busy,
  onAction,
}: {
  review?: Review;
  busy: boolean;
  onAction: (action: "process" | "ignore" | "reset" | "acknowledge" | "draft" | "save") => void;
}) {
  return (
    <StatusInput>
      <SelectField
        hideLabel
        label="Processing status"
        width="100%"
        compact
        value={review?.state || "unprocessed"}
        disabled={busy}
        options={reviewStatusOptions}
        onChange={(state) => {
          if (state !== (review?.state || "unprocessed"))
            onAction(
              state === "acknowledged"
                ? "acknowledge"
                : state === "drafted"
                  ? "draft"
                  : state === "saved"
                    ? "save"
                    : state === "processed"
                      ? "process"
                      : state === "ignored"
                        ? "ignore"
                        : "reset",
            );
        }}
      />
    </StatusInput>
  );
}
